import { useState } from 'react';
import { clsx } from 'clsx';
import {
  Bot,
  MessageSquare,
  Phone,
  ChevronDown,
  ChevronUp,
  Target,
} from 'lucide-react';
import { cn } from '@centraoneplus/ui';

export interface AICollectionMetadata {
  collectedVia: 'chat' | 'voice';
  sessionId?: string;
  collectedAt: string;
  confidence: number;
  fieldsCollected: string[];
}

export interface SentimentData {
  label: 'positive' | 'neutral' | 'negative' | 'frustrated';
  score: number;
  confidence: number;
}

export interface IntentData {
  primary: string;
  confidence: number;
  subIntents?: string[];
}

export interface AITicketMetadata {
  aiGenerated?: boolean;
  source?: 'chat_agent' | 'voice_agent';
  trigger?: string;
  sessionId?: string;
  categorySlug?: string;
  conversationSummary?: string;
  sentiment?: SentimentData;
  intent?: IntentData;
  unansweredQueries?: string[];
}

interface AICollectionContextProps {
  metadata: AITicketMetadata;
  aiMetadata?: AICollectionMetadata;
  className?: string;
}

const sentimentConfig: Record<
  SentimentData['label'],
  { label: string; emoji: string; color: string; bgColor: string }
> = {
  positive: {
    label: 'Positive',
    emoji: '😊',
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-100 dark:bg-green-900/30',
  },
  neutral: {
    label: 'Neutral',
    emoji: '😐',
    color: 'text-gray-600 dark:text-gray-400',
    bgColor: 'bg-gray-100 dark:bg-gray-700',
  },
  negative: {
    label: 'Negative',
    emoji: '😤',
    color: 'text-orange-600 dark:text-orange-400',
    bgColor: 'bg-orange-100 dark:bg-orange-900/30',
  },
  frustrated: {
    label: 'Frustrated',
    emoji: '😠',
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-100 dark:bg-red-900/30',
  },
};

function SentimentDisplay({ sentiment }: { sentiment: SentimentData }) {
  const config = sentimentConfig[sentiment.label] || sentimentConfig.neutral;
  const scorePercentage = Math.abs(sentiment.score) * 100;

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span className="text-gray-500 dark:text-gray-400 text-sm">Sentiment</span>
        <span
          className={cn(
            'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
            config.bgColor,
            config.color
          )}
        >
          <span>{config.emoji}</span>
          <span>{config.label}</span>
        </span>
      </div>
      <div className="flex items-center gap-1">
        <div className="w-16 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div
            className={cn(
              'h-full rounded-full',
              sentiment.score > 0 ? 'bg-green-500' : sentiment.score < -0.3 ? 'bg-red-500' : 'bg-orange-500'
            )}
            style={{ width: `${scorePercentage}%` }}
          />
        </div>
        <span className="text-xs text-gray-500 dark:text-gray-400">{sentiment.score.toFixed(1)}</span>
      </div>
    </div>
  );
}

function IntentDisplay({ intent }: { intent: IntentData }) {
  // Format intent for display (e.g., billing_issue -> Billing Issue)
  const formatIntent = (str: string) =>
    str
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Target className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
        <span className="text-gray-500 dark:text-gray-400 text-sm">Intent</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {formatIntent(intent.primary)}
        </span>
        <span className="text-xs text-gray-400 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded dark:text-gray-500">
          {Math.round(intent.confidence * 100)}%
        </span>
      </div>
    </div>
  );
}

export function AICollectionContext({
  metadata,
  aiMetadata,
  className,
}: AICollectionContextProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  // Don't render if not AI-generated
  if (!metadata.aiGenerated) {
    return null;
  }

  const channel = aiMetadata?.collectedVia || (metadata.source === 'voice_agent' ? 'voice' : 'chat');
  const ChannelIcon = channel === 'voice' ? Phone : MessageSquare;
  const channelLabel = channel === 'voice' ? 'Voice' : 'Chat';

  return (
    <div className={cn('border-b border-gray-100 dark:border-gray-700', className)}>
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
      >
        <div className="flex items-center gap-2">
          <Bot className="h-4 w-4 text-blue-500" />
          <span>AI Collection Context</span>
        </div>
        {isExpanded ? (
          <ChevronDown className="h-4 w-4 text-gray-400 dark:text-gray-500" />
        ) : (
          <ChevronUp className="h-4 w-4 text-gray-400 dark:text-gray-500" />
        )}
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 space-y-3">
          {/* Channel indicator */}
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500 dark:text-gray-400">Channel:</span>
            <span
              className={cn(
                'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium',
                channel === 'voice'
                  ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                  : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
              )}
            >
              <ChannelIcon className="h-3 w-3" />
              {channelLabel}
            </span>
          </div>

          <div className="h-px bg-gray-100 dark:bg-gray-700" />

          {/* Sentiment */}
          {metadata.sentiment && <SentimentDisplay sentiment={metadata.sentiment} />}

          {/* Intent */}
          {metadata.intent && <IntentDisplay intent={metadata.intent} />}

        </div>
      )}
    </div>
  );
}

export default AICollectionContext;
